<template>
    <div data-testid="step-PersonalData" class="container">
        <h1 class="container__personal-data-header">
            {{ $t(`${TRANSLATION_KEY}.personalDataHeader`) }}
        </h1>
        <p
            v-if="isSubheaderVisible"
            class="container__personal-data-p t-bold t-center"
            cy-data="uploadMessage"
        >
            {{ $t(`${TRANSLATION_KEY}.personalDataCheckData`) }}
        </p>

        <form
            ref="personalDataFormRef"
            :class="[isOpenPersonalDataLayer ? 'modify-form' : '']"
            @submit.prevent="null"
        >
            <transition name="fade" mode="out-in">
                <div>
                    <Loader
                        v-if="isAnimating"
                        :class="[
                            'loader--position-absolute',
                            'loader--background-white',
                            'loader--clear-transform',
                            isAnimating ? 'active' : '',
                        ]"
                    />

                    <div
                        v-if="isOpenPersonalDataLayer"
                        class="personal-data-layer"
                    >
                        <div class="personal-data-layer__row">
                            <span>{{ $t(`${TRANSLATION_KEY}.forename`) }}</span>
                            <div>
                                {{
                                    formData.firstName.length
                                        ? formData.firstName
                                        : $t(`${TRANSLATION_KEY}.noData`)
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row">
                            <span>{{ $t(`${TRANSLATION_KEY}.surname`) }}</span>
                            <div>
                                {{
                                    formData.lastName.length
                                        ? formData.lastName
                                        : $t(`${TRANSLATION_KEY}.noData`)
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row">
                            <span>{{
                                $t(`${TRANSLATION_KEY}.birthDate`)
                            }}</span>
                            <div>
                                {{
                                    formData.dateOfBirth.length
                                        ? formData.dateOfBirth
                                        : $t(`${TRANSLATION_KEY}.noData`)
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row">
                            <span>
                                {{
                                    formData.citizenship === 'PL' ||
                                    (null != formData.citizenship &&
                                        !formData.citizenship.length)
                                        ? $t(
                                              `${TRANSLATION_KEY}.personalIdNumber`
                                          )
                                        : $t(
                                              `${TRANSLATION_KEY}.secondPersonalIdNumber`
                                          )
                                }}
                            </span>
                            <div>
                                {{
                                    determineGenuineValue([
                                        formData.identificationNumber,
                                        formData.pesel,
                                    ])
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row">
                            <span>{{ $t(`${TRANSLATION_KEY}.idNumber`) }}</span>
                            <div>
                                {{
                                    formData.documentNumber.length
                                        ? formData.documentNumber
                                        : $t(`${TRANSLATION_KEY}.noData`)
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row">
                            <span>{{
                                $t(`${TRANSLATION_KEY}.documentExpiryDate`)
                            }}</span>
                            <div>
                                {{
                                    formData.documentExpirationDate.length
                                        ? formData.documentExpirationDate
                                        : $t(`${TRANSLATION_KEY}.noData`)
                                }}
                            </div>
                        </div>
                        <div class="personal-data-layer__row buttons-row">
                            <button
                                type="button"
                                class="button"
                                @click="isOpenPersonalDataLayer = false"
                            >
                                {{ $t(`${TRANSLATION_KEY}.correct`) }}
                            </button>
                            <SubmitButton
                                :action="Action.PERSONAL_DATA_CONFIRM"
                                :button-value="$t(`${TRANSLATION_KEY}.confirm`)"
                                :data="formData"
                                class="button button--submit button--green"
                            />
                        </div>
                    </div>
                </div>
            </transition>

            <div class="row">
                <Field
                    ref="firstNameField"
                    v-model="formData.firstName"
                    type="text"
                    name="firstName"
                    cy-data="name"
                    :placeholder="$t(`${TRANSLATION_KEY}.forenamePlaceholder`)"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.forename`) }}
                    </span>
                </Field>
            </div>
            <div class="row">
                <Field
                    ref="lastNameField"
                    v-model="formData.lastName"
                    type="text"
                    name="lastName"
                    cy-data="surname"
                    :placeholder="$t(`${TRANSLATION_KEY}.surnamePlaceholder`)"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.surname`) }}
                    </span>
                </Field>
            </div>
            <div class="row row--date-of-birth">
                <DateField
                    v-model="formData.dateOfBirth"
                    name="dateOfBirth"
                    cy-data="date-of-birth"
                    :label="$t(`${TRANSLATION_KEY}.birthDate`)"
                    @blur="validateDateOfBirth"
                />
            </div>
            <div class="row custom-select-wrapper">
                <TheCountrySelect
                    name="citizenship"
                    :default-value="formData.citizenship"
                    :country-list="citizenship"
                    :label="$t(`${TRANSLATION_KEY}.citizenshipLabel`)"
                    :placeholder="
                        $t(`${TRANSLATION_KEY}.citizenshipPlaceholder`)
                    "
                    class="custom-select"
                    @selected-country="formData.citizenship = $event"
                />
            </div>
            <div v-if="isProhibitedCitizenship" class="row">
                <p class="new-account__prohibited-warning">
                    {{ $t(`${TRANSLATION_KEY}.prohibitedCountryInfo`) }}
                </p>
            </div>

            <div class="row custom-select-wrapper">
                <Select
                    v-model="formData.documentType"
                    name="documentType"
                    :item-list="documentTypes"
                    :label="$t(`${TRANSLATION_KEY}.documentTypeOne`)"
                    :placeholder="$t(`${TRANSLATION_KEY}.documentTypeOne`)"
                    cy-data="document-type"
                    @update:model-value="formData.documentType = $event"
                />
            </div>
            <div class="row">
                <Field
                    ref="documentNumberField"
                    v-model="formData.documentNumber"
                    type="text"
                    name="documentNumber"
                    :placeholder="$t(`${TRANSLATION_KEY}.idNumberPlaceholder`)"
                    cy-data="document-number"
                    :no-auto-validation="true"
                    @blur="handleValidateDocumentField"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.idNumber`) }}
                    </span>
                </Field>
            </div>
            <div
                v-if="!formData.noLimitExpiryDate"
                class="row row--document-expiry-date"
            >
                <DateField
                    v-model="formData.documentExpirationDate"
                    cy-data="date-of-expiration"
                    :name="DateFieldNames.EXPIRATION"
                    :label="$t(`${TRANSLATION_KEY}.documentExpiryDate`)"
                    @blur="validateDateOfExpiration"
                />
            </div>
            <div
                v-if="
                    formData.documentType !== '2' &&
                    formData.citizenship !== 'DE'
                "
                class="row row--is-never-expiration-document"
            >
                <CheckBox
                    v-model="formData.noLimitExpiryDate"
                    name="noLimitExpiryDate"
                    :value="false"
                    cy-data="no-expiry-date"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.expirationDocumentText`) }}
                    </span>
                </CheckBox>
            </div>
            <div v-if="shouldShowIdentificationNumberField()" class="row">
                <div
                    v-if="getPersonalIdNumberDescription()"
                    class="personal-id-number-description"
                >
                    {{ personalIdNumberDescription }}
                </div>
                <Field
                    v-if="
                        (UtilityHelper.getLocale() === Locale.PL &&
                            null !== formData.citizenship &&
                            !formData.citizenship.length) ||
                        (null !== formData.citizenship &&
                            formData.citizenship.toLowerCase() === Locale.PL)
                    "
                    key="pesel"
                    ref="peselField"
                    v-model="formData.pesel"
                    type="text"
                    name="pesel"
                    :max-length="11"
                    :placeholder="$t(`${TRANSLATION_KEY}.number`)"
                    cy-data="pesel"
                    :is-numeric="true"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.number`) }}
                    </span>
                </Field>
                <Field
                    v-else
                    key="identificationNumber"
                    ref="identificationNumberField"
                    v-model="formData.identificationNumber"
                    type="text"
                    name="identificationNumber"
                    :placeholder="$t(`${TRANSLATION_KEY}.number`)"
                    :is-validated="isValidateIdentificationField"
                    cy-data="tin"
                    :is-numeric="numericalID ? true : false"
                >
                    <span>
                        {{ $t(`${TRANSLATION_KEY}.number`) }}
                    </span>
                </Field>
            </div>
            <div class="row custom-select-wrapper">
                <TheCountrySelect
                    name="birthPlaceCountry"
                    :default-value="defaultBirthPlaceCountry"
                    :country-list="birthPlaceCountries"
                    :label="$t(`${TRANSLATION_KEY}.birthPlaceCountry`)"
                    :placeholder="$t(`${TRANSLATION_KEY}.birthPlaceCountry`)"
                    class="custom-select"
                    @selected-country="formData.birthPlaceCountry = $event"
                />
            </div>
            <div v-if="isProhibitedBirthPlaceCountry" class="row">
                <p class="new-account__prohibited-warning">
                    {{ $t(`${TRANSLATION_KEY}.prohibitedCountryInfo`) }}
                </p>
            </div>
            <SubmitButton
                :action="Action.PERSONAL_DATA_CONFIRM"
                :button-value="$t(`${TRANSLATION_KEY}.next`)"
                data-testid="btn-riskAssessment-nextStep"
                :data="formData"
                class="button button--submit"
                :disabled="!formIsValid"
                @click="
                    () =>
                        amplitudeSendEvent(
                            AmplitudeAnalyticsEvents.personalDetails
                                .PERSONAL_DETAILS_CONTINUE
                        )
                "
            />
        </form>
    </div>
</template>

<script lang="ts">
import {
    defineComponent,
    onBeforeMount,
    onMounted,
    onBeforeUnmount,
    ref,
    reactive,
    watch,
    computed,
    nextTick,
} from 'vue';
import { useStore } from 'vuex';
import i18n from '@/i18n';
import { DataHelper } from '@/helpers/DataHelper';
import { DocumentType } from '@/constants/form-values';
import { Locale } from '@/constants/locale';
import { UtilityHelper } from '@/helpers/UtilityHelper';

import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import CheckBox from '@/components/basic/fields/CheckBox.vue';
import Field from '@/components/basic/fields/Field.vue';
import DateField from '@/components/compositeFields/DateField.vue';
import Select from '@/components/basic/fields/Select.vue';
import Loader from '@/components/basic/Loader.vue';
import TheCountrySelect from '@/components/basic/fields/TheCountrySelect.vue';

import { SET_PROGRESS } from '@/constants/mutation-types';
import { PERSONAL_ID_IGNORE_COUNTRIES } from '@/constants/country-settings';
import { useI18n } from 'vue-i18n';
import { countryToAlpha2 } from 'country-to-iso';
import {
    amplitudeSendEvent,
    AmplitudeAnalyticsEvents,
} from '@/utils/AmplitudeAnalyticsEvents';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';
import { Action } from '@/constants/ajax';
import type { FieldInterface } from '@/types';

enum DateFieldNames {
    BIRTH = 'dateOfBirth',
    EXPIRATION = 'documentExpirationDate',
}

enum KeyWords {
    PATH = 'path',
    IS_RETURNING = 'isReturning',
    PERSONAL_DATA_CONFIRM = 'personalDataConfirm',
}

interface ClientIdExtractedField {
    Name: string;
    Value: string;
}

interface ClientIdDataEntry {
    ExtractedFields?: ClientIdExtractedField[];
    IssuingCountryCode?: string;
}

export default defineComponent({
    name: 'PersonalData',
    components: {
        CheckBox,
        Field,
        Select,
        SubmitButton,
        DateField,
        Loader,
        TheCountrySelect,
    },
    setup() {
        const store = useStore();
        const { t } = useI18n();

        const clientIdData = computed<ClientIdDataEntry[]>(
            () => (store.state.clientIdData || []) as ClientIdDataEntry[]
        );
        const userResidence = computed(() => store.state.residenceCountry);

        const formData = reactive({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            documentExpirationDate: '',
            citizenship: '',
            birthPlaceCountry: '',
            identificationNumber: '',
            pesel: '',
            noPesel: false,
            documentType: '',
            documentNumber: '',
            noLimitExpiryDate: false,
        });

        const isValidateIdentificationField = ref(true);
        const documentExpirationDate = ref('');
        const isDateFieldFilled = ref(false);
        const isSubheaderVisible = ref(false);
        const isOpenPersonalDataLayer = ref(false);
        const isAnimating = ref(false);
        const formIsValid = ref(false);
        const numericalID = ref(false);

        const firstNameField = ref<null | any>(null);
        const lastNameField = ref<null | any>(null);
        const documentNumberField = ref<null | any>(null);
        const peselField = ref<null | any>(null);
        const identificationNumberField = ref<null | any>(null);

        const TRANSLATION_KEY = 'steps.PersonalData';

        const isProhibitedCitizenship = computed(() => {
            return store.state.config.prohibitedCitizenship.includes(
                formData.citizenship
            );
        });

        const isProhibitedBirthPlaceCountry = computed(() => {
            return store.state.config.prohibitedCitizenship.includes(
                formData.birthPlaceCountry
            );
        });

        const isProhibitedCountry = computed(
            () =>
                isProhibitedCitizenship.value ||
                isProhibitedBirthPlaceCountry.value
        );

        const months = ref<Record<string, string> | string[]>([]);

        function validateDateOfBirth() {
            store.dispatch('validate', {
                name: DateFieldNames.BIRTH,
                value: formData.dateOfBirth,
            });
        }

        function validateDateOfExpiration() {
            store.dispatch('validate', {
                name: DateFieldNames.EXPIRATION,
                value: formData.documentExpirationDate,
            });
        }

        onBeforeUnmount(() => {
            if (window.localStorage.getItem(KeyWords.PATH)) {
                window.localStorage.removeItem(KeyWords.PATH);
            }
            if (window.localStorage.getItem(KeyWords.IS_RETURNING)) {
                window.localStorage.removeItem(KeyWords.IS_RETURNING);
            }
        });

        onBeforeMount(async () => {
            if (
                window.localStorage.getItem(KeyWords.PATH) &&
                !window.localStorage.getItem(KeyWords.IS_RETURNING) &&
                store.state.step === KeyWords.PERSONAL_DATA_CONFIRM
            ) {
                isAnimating.value = true;

                await UtilityHelper.sleep(1500);

                const waitForFormData = setInterval(() => {
                    if (
                        formData.firstName.length &&
                        formData.documentNumber.length
                    ) {
                        const formHasErrors = checkWhetherFormHasErrors();
                        if (formHasErrors) {
                            isAnimating.value = false;
                        } else {
                            if (
                                formData.pesel.length ||
                                formData.identificationNumber.length
                            ) {
                                isOpenPersonalDataLayer.value = true;
                                isAnimating.value = false;
                            } else {
                                isAnimating.value = false;
                            }
                        }
                        clearInterval(waitForFormData);
                    }
                }, 100);
            }
        });

        onMounted(() => {
            if (store.state.timeToFinish >= 7) {
                store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.PERSONAL_DATA);
            }
            isSubheaderVisible.value = isDocumentUploaded.value;
            isNumericalId();
            setFormData();
            isValid();
            setInterval(isValid, 100);
            validatePrefilledFields();
        });

        watch(
            () => clientIdData.value,
            (newValue) => {
                if (Array.isArray(newValue) && newValue.length) {
                    setFormData();
                }
            }
        );

        watch(
            () => formData.identificationNumber,
            (newValue) => {
                if (
                    newValue &&
                    newValue.length &&
                    formData.citizenship !== 'PL'
                ) {
                    formData.noPesel = true;
                    if ((formData.pesel || '').length) {
                        formData.pesel = '';
                    }
                }
            }
        );

        watch(
            () => formData.citizenship,
            (newValue) => {
                if (newValue) {
                    store.commit('DELETE_ERROR', 'citizenship');
                    store.dispatch('validate', {
                        name: 'citizenship',
                        value: newValue,
                    });
                }

                const numericCountries =
                    'be, bg, cz, dk, ee, gr, hr, lt, lv, pl, pt, ro, se, se, si, sk, is, no';
                numericalID.value = numericCountries.includes(
                    formData.citizenship.toLowerCase()
                );

                if (
                    newValue &&
                    newValue.length &&
                    formData.citizenship === 'GR'
                ) {
                    isValidateIdentificationField.value = false;
                } else {
                    isValidateIdentificationField.value = true;
                }

                if (
                    newValue &&
                    newValue.length &&
                    formData.citizenship !== 'PL'
                ) {
                    formData.pesel = '';
                    formData.noPesel = true;
                } else {
                    formData.noPesel = false;
                }

                if (
                    newValue &&
                    newValue.length &&
                    formData.citizenship == 'DE'
                ) {
                    formData.noLimitExpiryDate = false;
                }

                if (formData.documentNumber.length) {
                    handleValidateDocumentField();
                }
            }
        );

        watch(
            () => formData.documentType,
            () => {
                if (
                    formData.documentType === '2' &&
                    formData.noLimitExpiryDate
                ) {
                    formData.noLimitExpiryDate = false;
                }

                if (formData.documentNumber.length && formData.citizenship) {
                    handleValidateDocumentField();
                }
            }
        );

        watch(
            () => formData.pesel,
            (newValue) => {
                if (
                    newValue &&
                    newValue.length &&
                    (formData.identificationNumber || '').length &&
                    formData.citizenship === 'PL'
                ) {
                    formData.identificationNumber = '';
                    if (formData.noPesel) {
                        formData.noPesel = false;
                    }
                }
                if (
                    newValue &&
                    newValue.length &&
                    (formData.identificationNumber || '').length &&
                    formData.citizenship !== 'PL'
                ) {
                    formData.pesel = '';
                }
            }
        );

        watch(
            () => formData.noLimitExpiryDate,
            (newValue) => {
                if (newValue) {
                    documentExpirationDate.value =
                        formData.documentExpirationDate;
                    formData.documentExpirationDate = '';
                } else {
                    formData.documentExpirationDate =
                        documentExpirationDate.value.replace(/X/gi, '');
                }
            }
        );

        watch(
            () => store.state.formData,
            (newValue) => {
                if (newValue && newValue.citizenship) {
                    Object.assign(formData, newValue);

                    if (newValue.documentType !== undefined) {
                        formData.documentType = String(newValue.documentType);
                    }
                }
            },
            { immediate: true, deep: true }
        );

        const citizenship = computed(() => store.state.config.citizenship);
        const defaultBirthPlaceCountry = computed(() =>
            String(store.state.formData.birthPlaceCountry || '')
        );
        const birthPlaceCountries = computed((): FieldInterface[] => {
            const countries = store.state.config.countries || [];
            const defaultCountry = String(
                userResidence.value || store.state.formData.country || ''
            );

            if (!defaultCountry) return countries;

            const defaultCountryOption = countries.find(
                (country: FieldInterface) =>
                    String(country.key) === defaultCountry
            );

            if (!defaultCountryOption) return countries;

            return [
                defaultCountryOption,
                ...countries.filter(
                    (country: FieldInterface) =>
                        String(country.key) !== defaultCountry
                ),
            ];
        });
        const isDocumentUploaded = computed(
            () => store.state.formData.uploadAction === 1
        );
        const stepName = computed(() => store.state.step);

        const documentTypes = computed(() => {
            if (!isEuropeanCountry()) {
                return [
                    {
                        key: DocumentType.ID,

                        value: t('steps.PersonalData.documentTypeThree'),
                    },
                    {
                        key: DocumentType.PASSPORT,
                        value: t(
                            'steps.PersonalData.documentTypeTwo'
                        ) as string,
                    },
                    {
                        key: DocumentType.OTHER,
                        value: t(
                            'steps.PersonalData.documentTypeFour'
                        ) as string,
                    },
                ];
            } else {
                return [
                    {
                        key: DocumentType.PASSPORT,
                        value: t(
                            'steps.PersonalData.documentTypeTwo'
                        ) as string,
                    },
                    {
                        key: DocumentType.ID,
                        value: t(
                            'steps.PersonalData.documentTypeThree'
                        ) as string,
                    },
                    {
                        key: DocumentType.OTHER,
                        value: t(
                            'steps.PersonalData.documentTypeFour'
                        ) as string,
                    },
                ];
            }
        });

        function normalizeDate(dateString: string): string {
            if (dateString.includes('-')) {
                return dateString;
            } else if (dateString.includes('.')) {
                const [day, month, year] = dateString.split('.');
                return `${year}-${month}-${day}`;
            }
            return '';
        }

        const personalIdNumberDescription = computed(() => {
            return getPersonalIdNumberDescription();
        });

        function setFormData() {
            if (!Array.isArray(clientIdData.value)) {
                return;
            }

            if (!getExtractedFieldValue('NationalityName')) {
                return;
            }

            if (getExtractedFieldValue('FirstName')) {
                formData.firstName = getExtractedFieldValue('FirstName');
            }

            if (getExtractedFieldValue('LastName')) {
                formData.lastName = getExtractedFieldValue('LastName');
            }

            const firstClientIdData = clientIdData.value[0];
            if (firstClientIdData?.IssuingCountryCode?.length) {
                const citizenshipCode = countryToAlpha2(
                    firstClientIdData.IssuingCountryCode
                );
                formData.citizenship =
                    typeof citizenshipCode === 'string' ? citizenshipCode : '';
            }

            const personalNumber = getExtractedFieldValue('PersonalNumber');
            const optionalValue2 = getExtractedFieldValue('OptionalValue2');

            if (formData.citizenship === 'PL') {
                formData.pesel = personalNumber || optionalValue2 || '';
            } else {
                formData.identificationNumber =
                    personalNumber || optionalValue2 || '';
            }

            const documentType = getExtractedFieldValue('DocumentType');
            if (documentType == 'National Identification Card') {
                formData.documentType = '1';
            } else if (documentType == 'Passport') {
                formData.documentType = '2';
            } else {
                formData.documentType = '3';
            }

            if (getExtractedFieldValue('DocumentNumber')) {
                formData.documentNumber =
                    getExtractedFieldValue('DocumentNumber');
            }

            if (getExtractedFieldValue('BirthDate')) {
                formData.dateOfBirth = normalizeDate(
                    getExtractedFieldValue('BirthDate')
                );
            }

            if (getExtractedFieldValue('ExpiryDate')) {
                formData.documentExpirationDate = normalizeDate(
                    getExtractedFieldValue('ExpiryDate')
                );
            }

            validatePrefilledFields();
            store.commit('SET_FORM_DATA', formData);
        }

        function validatePrefilledFields() {
            nextTick(() => {
                if (formData.firstName.length && firstNameField.value) {
                    firstNameField.value.validate();
                }
                if (formData.lastName.length && lastNameField.value) {
                    lastNameField.value.validate();
                }
                if (
                    formData.documentNumber.length &&
                    documentNumberField.value
                ) {
                    documentNumberField.value.validate();
                }
                if (formData.citizenship === 'PL') {
                    if (formData.pesel.length && peselField.value) {
                        peselField.value.validate();
                    }
                } else if (
                    formData.identificationNumber?.length &&
                    identificationNumberField.value
                ) {
                    identificationNumberField.value.validate();
                }
            });
        }

        function getExtractedFieldValue(fieldName: string): string {
            if (clientIdData.value.length !== 0) {
                for (const data of clientIdData.value) {
                    if (!data || !data.ExtractedFields) continue;
                    const field = data.ExtractedFields.find(
                        (field) => field.Name === fieldName
                    );
                    if (field) return field.Value;
                }
            }
            return '';
        }

        function isValid() {
            const labels = document.querySelectorAll(
                'form .row:not(.row--is-never-expiration-document) label'
            );
            const customSelects = document.querySelectorAll(
                '.custom-select-wrapper .custom-select'
            );
            const labelsLength = labels.length;
            const selectsLength = customSelects.length;
            let validLabelsLength = 0;
            let validSelectsLength = 0;

            customSelects.forEach((select) => {
                if (select.classList.contains('valid')) {
                    validSelectsLength++;
                }
            });
            labels.forEach((label) => {
                if (label.classList.contains('valid')) {
                    validLabelsLength++;
                }
            });

            if (!isValidateIdentificationField.value) {
                validLabelsLength++;
            }

            if (
                validLabelsLength === labelsLength &&
                validSelectsLength === selectsLength &&
                !isProhibitedCountry.value
            ) {
                formIsValid.value = true;
            } else {
                formIsValid.value = false;
            }
        }

        function determineGenuineValue(arr: string[]): string {
            let genuineValue = '';
            for (let i = 0, length = arr.length; i < length; i++) {
                if (arr[i].length) {
                    genuineValue = arr[i];
                }
            }
            return genuineValue;
        }

        function checkWhetherFormHasErrors(): boolean {
            const hardcodedErrorNames = [
                'firstName',
                'lastName',
                'dateOfBirth',
                'citizenship',
                'pesel',
                'identificationNumber',
                'documentType',
                'documentNumber',
                'documentExpirationDate',
            ];
            let isError = false;
            hardcodedErrorNames.forEach((errorName) => {
                if (store.state.errors.has(errorName)) {
                    isError = true;
                }
            });
            return isError;
        }

        function loadMonthsTranslations() {
            DataHelper.loadJSON(i18n.global.locale.value).then((data) => {
                Object.entries(data).forEach(([key, value]) => {
                    if (key === 'months') {
                        if (
                            Array.isArray(value) ||
                            (typeof value === 'object' && value !== null)
                        ) {
                            months.value = value as
                                | Record<string, string>
                                | string[];
                        }
                    }
                });
            });
        }

        function isEuropeanCountry(): boolean {
            return store.state.config.europeanCountries.includes(
                formData.citizenship
            );
        }

        function shouldShowIdentificationNumberField(): boolean {
            if (!isEuropeanCountry() && formData.documentType === '2') {
                return false;
            }

            if (
                formData.citizenship &&
                PERSONAL_ID_IGNORE_COUNTRIES.includes(formData.citizenship)
            ) {
                return false;
            }

            return true;
        }

        function showIdentificationNumberField(): boolean {
            if (formData.citizenship && formData.citizenship.length) {
                if (
                    PERSONAL_ID_IGNORE_COUNTRIES.indexOf(
                        formData.citizenship
                    ) !== -1
                )
                    return false;
            }
            return true;
        }

        function getPersonalIdNumberDescription(): any {
            if (!showIdentificationNumberField()) return null;
            if (formData.citizenship && formData.citizenship.length) {
                let code = formData.citizenship;
                code = code[0] + code.slice(1).toLowerCase();
                const translationKey = `steps.PersonalData.personalIdNumberDescription${code}`;
                return t(translationKey) !== translationKey
                    ? t(translationKey)
                    : t('steps.PersonalData.personalIdNumberDescription');
            }
            return null;
        }

        function isNumericalId() {
            const numericCountries =
                'be, bg, cz, dk, ee, gr, hr, lt, lv, pl, pt, ro, se, se, sl, sk';
            numericalID.value = numericCountries.includes(
                UtilityHelper.getLocale()
            );
        }

        async function handleValidateDocumentField() {
            store.dispatch('validate', {
                documentType: formData.documentType,
                documentNumber: formData.documentNumber,
                citizenship: formData.citizenship,
            });
        }

        return {
            formData,
            isValidateIdentificationField,
            documentExpirationDate,
            isDateFieldFilled,
            isSubheaderVisible,
            isOpenPersonalDataLayer,
            isAnimating,
            formIsValid,
            numericalID,
            months,
            UtilityHelper,

            TRANSLATION_KEY,
            DateFieldNames,
            KeyWords,
            Action,
            Locale,

            citizenship,
            defaultBirthPlaceCountry,
            birthPlaceCountries,
            isDocumentUploaded,
            stepName,
            documentTypes,
            personalIdNumberDescription,

            firstNameField,
            lastNameField,
            documentNumberField,
            peselField,
            identificationNumberField,
            isProhibitedCountry,
            isProhibitedCitizenship,
            isProhibitedBirthPlaceCountry,

            isValid,
            determineGenuineValue,
            checkWhetherFormHasErrors,
            loadMonthsTranslations,
            isEuropeanCountry,
            showIdentificationNumberField,
            getPersonalIdNumberDescription,
            isNumericalId,
            validateDateOfBirth,
            validateDateOfExpiration,
            shouldShowIdentificationNumberField,
            amplitudeSendEvent,
            AmplitudeAnalyticsEvents,
            userResidence,
            handleValidateDocumentField,
        };
    },
});
</script>
